import React, { FC, useState, useEffect, useCallback, memo, useRef } from 'react';
import Cropper, { Area } from 'react-easy-crop';

import { Backdrop, Box, Button, Stack } from '@mui/material';

import { getCroppedImg, createImage } from '@shared/utils/file-utils';
import { compressImageToWebP } from '@shared/utils/image-compressor';

import styles from './cropper-overlay.module.scss';

// Функция для применения круглой маски к изображению с сохранением прозрачности
const applyCircularMask = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Не удалось получить контекст canvas'));
      return;
    }

    img.onload = () => {
      // Устанавливаем размер canvas равным размеру изображения
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Очищаем canvas полностью прозрачным
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Создаем круглую маску
      ctx.beginPath();
      const radius = Math.min(img.width, img.height) / 2;
      const centerX = img.width / 2;
      const centerY = img.height / 2;
      
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.clip();
      
      // Рисуем изображение
      ctx.drawImage(img, 0, 0);
      
      // Конвертируем в blob и создаем новый файл
      canvas.toBlob((blob) => {
        if (blob) {
          const maskedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '') + '_circular.png', {
            type: 'image/png'
          });
          resolve(maskedFile);
        } else {
          reject(new Error('Не удалось создать blob'));
        }
      }, 'image/png', 1.0); // Максимальное качество для сохранения прозрачности
    };
    
    img.onerror = () => reject(new Error('Не удалось загрузить изображение'));
    img.src = URL.createObjectURL(file);
  });
};

export interface CropperOverlayProps {
  onClose: () => void;
  onConfirm: (image: File | string | null) => void;
  file: File | undefined;
  isOpen: boolean;
  imageSrc: string;
  maskImage: string;
  aspectRatio: number;
  fileName: string;
  onCroppedPixelsChange: (fieldName: string, croppedArea: Area | null) => void;
  fieldName: string;
  gameType?: 'memory' | 'miner' | 'maze' | 'slot' | 'capybara' | 'flappy';
  hasTransparency?: boolean;
}

const CropperOverlay: FC<CropperOverlayProps> = ({
  onConfirm,
  onClose,
  file,
  isOpen,
  imageSrc,
  maskImage,
  aspectRatio,
  fileName,
  onCroppedPixelsChange,
  fieldName,
  gameType,
  hasTransparency = false,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [canvasImage, setCanvasImage] = useState<HTMLImageElement>();
  const [croppedPixels, setCroppedPixels] = useState<Area>();

  // Счетчик количества обрезок
  const [cropCount, setCropCount] = useState(0);

  const fieldNameRef = useRef(fieldName);

useEffect(() => {
  fieldNameRef.current = fieldName;
}, [fieldName]);


  useEffect(() => {
    if (!isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedPixels(undefined);
    }
  }, [isOpen]);

  useEffect(() => {
    if (imageSrc) {
      createImage(imageSrc).then(setCanvasImage).catch(console.error);
    }
  }, [imageSrc]);

  const handleCropComplete = (croppedArea: Area, croppedPixels: Area) => {
    setCroppedPixels(croppedPixels);
    onCroppedPixelsChange(fieldNameRef.current, croppedPixels);
  };

  const handleSaveClick = () => {
    onCroppedPixelsChange(fieldName, null);

    if (!croppedPixels) {
      onConfirm(file || imageSrc);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCroppedPixels(undefined);
      onClose();

      return;
    }

    if (canvasImage) {
      /// Увеличиваем счетчик обрезок
    setCropCount((prevCount) => {
      const newCount = prevCount + 1;
      return newCount;
    });

      // Создаём новый canvas для обрезки
      getCroppedImg(canvasImage, croppedPixels, fileName, 0, hasTransparency)
        .then(async (croppedImage) => {
          console.log('Original cropped image:', croppedImage);
          
          // Для Капибары применяем дополнительное сжатие в WebP
          if (gameType === 'capybara') {
            try {
              const compressionResult = await compressImageToWebP(croppedImage);
              console.log('WebP compression result:', {
                originalSize: compressionResult.originalSize,
                compressedSize: compressionResult.compressedSize,
                compressionRatio: compressionResult.compressionRatio.toFixed(2) + '%'
              });
              onConfirm(compressionResult.file);
            } catch (error) {
              console.error('WebP compression failed, using original:', error);
              onConfirm(croppedImage);
            }
          } 
          // Для Flappy Bird применяем только круглую маску без WebP сжатия для сохранения прозрачности
          else if (gameType === 'flappy') {
            try {
              // Применяем круглую маску
              const circularMaskedImage = await applyCircularMask(croppedImage);
              console.log('Flappy circular mask applied:', {
                fileName: circularMaskedImage.name,
                fileSize: circularMaskedImage.size,
                fileType: circularMaskedImage.type
              });
              // НЕ применяем WebP сжатие для сохранения прозрачности
              onConfirm(circularMaskedImage);
            } catch (error) {
              console.error('Flappy processing failed, using original:', error);
              onConfirm(croppedImage);
            }
          } else {
            onConfirm(croppedImage);
          }
          
          setZoom(1);
          setCrop({ x: 0, y: 0 });
          setTimeout(() => onClose(), 50);
        })
        .catch(console.error);
    }
  };

  return (
    <Backdrop className={styles.backDrop} open={isOpen}>
      <Stack className={styles.backdropStack} spacing={1}>
        <Box component="div" className={styles.cropArea}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            zoomSpeed={0.25}
            style={{
              cropAreaStyle: {
                ...(maskImage && gameType === 'flappy'
                  ? {
                      borderRadius: '50%',
                      border: '2px solid #2196F3',
                      boxShadow: '0 0 10px rgba(33, 150, 243, 0.5)',
                    }
                  : maskImage
                  ? {
                      backgroundImage: `url(${maskImage})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }
                  : {}),
              },
            }}
            objectFit="vertical-cover"
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </Box>
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={handleSaveClick}
        >
          Save
        </Button>
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={onClose}
        >
          Close
        </Button>
      </Stack>
    </Backdrop>
  );
};

export default CropperOverlay;
